import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import type { AdminArticleListItem } from "@/types/article";
import { useDeleteArticle } from "@/hooks/use-articles";

interface DeleteDialogProps {
  article: AdminArticleListItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteDialog({
  article,
  open,
  onOpenChange,
}: DeleteDialogProps) {
  const { mutate: deleteArticle, isPending } = useDeleteArticle();

  const handleConfirm = () => {
    deleteArticle(article._id, {
      onSuccess: () => onOpenChange(false),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>아티클 삭제</DialogTitle>
          <DialogDescription>
            이 아티클을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">제목</span>
            <span className="text-sm truncate max-w-[200px]">
              {article.title.ko}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">작성자</span>
            <span className="text-sm">{article.authorInfo.name.ko}</span>
          </div>
        </div>

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            삭제된 아티클은 복구할 수 없습니다. 발행된 아티클의 경우 사용자에게도 더 이상 표시되지 않습니다.
          </AlertDescription>
        </Alert>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            취소
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isPending}
          >
            {isPending ? "삭제 중..." : "삭제 확인"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
